/**
 * DJI R-JPEG Thermal Image Parser — Pure JavaScript / Browser-only
 *
 * Extracts temperature data from DJI drone thermal R-JPEG images without
 * requiring the native DJI Thermal SDK or any server. Supports cameras:
 * H20T, H20N, H30T, M2EA, M3T, M3TD, M30T, M4T, XTS.
 *
 * R-JPEG binary layout:
 *   APP3 (FF E3) × N segments → RAW16 thermal sensor data (uint16 LE)
 *   APP4 (FF E4)              → Calibration params + camera ID
 *   APP5 (FF E5)              → Curve LUT (raw → temp × 10)
 *
 * REFERENCE: DJI Thermal SDK v1.8, dirp_api.h
 */

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface RadiometricParams {
  emissivity: number;        // 0.10 – 1.00 (default 0.95)
  distance_m: number;        // meters (default 10)
  humidity_pct: number;      // 0 – 100 (default 40)
  air_temp_c: number;        // Celsius (default 30)
  reflected_temp_c: number;  // Celsius (default 25)
}

export const DEFAULT_PARAMS: RadiometricParams = {
  emissivity: 0.95,
  distance_m: 10,
  humidity_pct: 40,
  air_temp_c: 30,
  reflected_temp_c: 25,
};

export interface RoiStats {
  min_c: number;
  max_c: number;
  avg_c: number;
  std_c: number;
  n: number;
  /** Image-pixel X of the hottest pixel in the ROI */
  max_x: number;
  /** Image-pixel Y of the hottest pixel in the ROI */
  max_y: number;
  /** Reference temperature — median of all pixels in the ROI (represents normal cell temp) */
  ref_c: number;
  /** ΔT = max_c − ref_c (hotspot severity metric per IEC 62446-3) */
  delta_t: number;
}

export interface ThermalImage {
  width: number;
  height: number;
  /**
   * Visible JPEG dimensions from the SOF frame header. Super-resolution IR
   * photos (e.g. M4T) save a 1280×1024 JPEG whose radiometric matrix is still
   * 640×512 — annotation coords live in JPEG space, so sampling must scale
   * through these. Equal to width/height on native-resolution photos (M3T).
   */
  displayWidth: number;
  displayHeight: number;
  cameraModel: string;
  rawData: Uint16Array;
  curveLut: Int16Array | null;
  temperatures: Float32Array;
  minTemp: number;
  maxTemp: number;
  avgTemp: number;
  embeddedParams: RadiometricParams;
}

// ---------------------------------------------------------------------------
// Internal: JPEG segment extraction
// ---------------------------------------------------------------------------

interface JpegSegment {
  marker: number; // e.g. 0xE3 for APP3
  data: Uint8Array;
}

/**
 * Scan a JPEG buffer and extract APP segments by marker type.
 * Returns segments in file order.
 */
function extractJpegSegments(buf: ArrayBuffer): JpegSegment[] {
  const u8 = new Uint8Array(buf);
  const segments: JpegSegment[] = [];
  let i = 0;

  // Verify SOI
  if (u8[0] !== 0xFF || u8[1] !== 0xD8) {
    throw new Error('Not a JPEG file (missing SOI marker)');
  }
  i = 2;

  while (i < u8.length - 1) {
    // Find next FF marker
    if (u8[i] !== 0xFF) { i++; continue; }

    const marker = u8[i + 1];

    // Skip padding FF bytes
    if (marker === 0xFF) { i++; continue; }

    // SOI / EOI / standalone markers (no length field)
    if (marker === 0xD8 || marker === 0xD9 || (marker >= 0xD0 && marker <= 0xD7) || marker === 0x00) {
      if (marker === 0xD9) break; // EOI — stop before second image in MPF
      i += 2;
      continue;
    }

    // SOS — scan data follows, skip until next marker
    if (marker === 0xDA) {
      // Read length of SOS header
      if (i + 3 >= u8.length) break;
      const sosLen = (u8[i + 2] << 8) | u8[i + 3];
      i += 2 + sosLen;
      // Skip entropy-coded data until next 0xFF not followed by 0x00
      while (i < u8.length - 1) {
        if (u8[i] === 0xFF && u8[i + 1] !== 0x00) break;
        i++;
      }
      continue;
    }

    // Regular marker with length
    if (i + 3 >= u8.length) break;
    const len = (u8[i + 2] << 8) | u8[i + 3]; // includes the 2 length bytes
    if (len < 2) { i += 2; continue; }

    const dataStart = i + 4; // after FF XX LL LL
    const dataLen = len - 2;
    if (dataStart + dataLen > u8.length) break;

    segments.push({
      marker,
      data: u8.slice(dataStart, dataStart + dataLen),
    });

    i += 2 + len;
  }

  return segments;
}

/**
 * Read the visible JPEG dimensions from the first SOF frame header.
 * extractJpegSegments stops at the first EOI, so this is always the main
 * (visible) image, never an MPF sub-image.
 */
function parseSofDimensions(segments: JpegSegment[]): { width: number; height: number } | null {
  // SOF0-SOF15 are 0xC0-0xCF minus DHT (0xC4), JPG (0xC8), DAC (0xCC)
  for (const s of segments) {
    if (s.marker >= 0xC0 && s.marker <= 0xCF &&
        s.marker !== 0xC4 && s.marker !== 0xC8 && s.marker !== 0xCC &&
        s.data.length >= 5) {
      const height = (s.data[1] << 8) | s.data[2];
      const width = (s.data[3] << 8) | s.data[4];
      if (width > 0 && height > 0) return { width, height };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Internal: APP4 parameter parsing (4 camera-specific formats)
// ---------------------------------------------------------------------------

interface ParsedCalibration {
  params: RadiometricParams;
  cameraModel: string;
  width: number;
  height: number;
}

function parseApp4(data: Uint8Array): ParsedCalibration {
  const dv = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const result: ParsedCalibration = {
    params: { ...DEFAULT_PARAMS },
    cameraModel: 'Unknown',
    width: 640,
    height: 512,
  };

  if (data.length < 16) return result;

  const magic = dv.getUint32(0, true); // little-endian

  // Format A: H20T / XTS — magic 0x061255AA (bytes: AA 55 12 06 LE)
  if (magic === 0x061255AA) {
    result.cameraModel = 'H20T';
    if (data.length >= 0x5A) {
      result.params.distance_m = dv.getUint16(0x44, true);
      result.params.humidity_pct = dv.getUint16(0x46, true);
      result.params.emissivity = dv.getUint16(0x48, true) / 100;
      result.params.reflected_temp_c = dv.getInt16(0x4A, true) / 10;
      result.params.air_temp_c = dv.getInt16(0x4C, true) / 10;
    }
    return result;
  }

  // Format B / D: modern cameras — magic 0xFFD1D2FF + identifier "iirp" or "hirp"
  if (magic === 0xFFD1D2FF || magic === 0xFFD2D1FF) {
    const id4 = String.fromCharCode(data[4], data[5], data[6], data[7]);

    if (id4 === 'iirp' || id4 === 'hirp') {
      result.cameraModel = id4 === 'hirp' ? 'M3TD' : 'Modern';
      if (data.length >= 0x34) {
        result.params.air_temp_c = dv.getFloat32(0x20, true);
        result.params.distance_m = dv.getFloat32(0x24, true);
        result.params.emissivity = dv.getFloat32(0x28, true);
        const humFraction = dv.getFloat32(0x2C, true);
        result.params.humidity_pct = humFraction <= 1.0 ? humFraction * 100 : humFraction;
        result.params.reflected_temp_c = dv.getFloat32(0x30, true);
      }
      // Try to read camera ID string at offset 0x85
      if (data.length >= 0x95) {
        const idBytes: number[] = [];
        for (let j = 0x85; j < Math.min(0x95, data.length); j++) {
          if (data[j] === 0) break;
          idBytes.push(data[j]);
        }
        if (idBytes.length > 0) {
          result.cameraModel = String.fromCharCode(...idBytes);
        }
      }
      return result;
    }
  }

  // Format C: M2EA — no magic header, direct float32 params
  // Heuristic: check if first float looks like a reasonable temperature (-50 to 80)
  const firstFloat = dv.getFloat32(0, true);
  if (firstFloat > -50 && firstFloat < 80) {
    result.cameraModel = 'M2EA';
    result.params.air_temp_c = firstFloat;
    result.params.distance_m = dv.getFloat32(0x04, true);
    result.params.emissivity = dv.getFloat32(0x08, true);
    const humFraction = dv.getFloat32(0x0C, true);
    result.params.humidity_pct = humFraction <= 1.0 ? humFraction * 100 : humFraction;
    result.params.reflected_temp_c = dv.getFloat32(0x10, true);
    // Camera ID at offset 0x65
    if (data.length >= 0x75) {
      const idBytes: number[] = [];
      for (let j = 0x65; j < Math.min(0x75, data.length); j++) {
        if (data[j] === 0) break;
        idBytes.push(data[j]);
      }
      if (idBytes.length > 0) {
        result.cameraModel = String.fromCharCode(...idBytes);
      }
    }
    return result;
  }

  return result;
}

// ---------------------------------------------------------------------------
// Internal: APP5 curve LUT parsing
// ---------------------------------------------------------------------------

function parseCurveLut(data: Uint8Array): Int16Array | null {
  if (data.length < 4) return null;

  const dv = new DataView(data.buffer, data.byteOffset, data.byteLength);

  // Some cameras have a 4-byte header before the LUT entries
  // Try to detect: if first 4 bytes look like a reasonable count/header, skip them
  let offset = 0;

  // H20T: 16384 entries × 2 bytes = 32768 bytes → data.length ~= 32768
  // M3T: variable length with a header
  if (data.length >= 32768) {
    // Likely H20T-style: raw int16 entries starting at offset 0
    offset = 0;
  } else if (data.length > 8) {
    // Check if there's a header: first uint32 might be entry count
    const possibleCount = dv.getUint32(0, true);
    if (possibleCount > 0 && possibleCount < 100000 && possibleCount * 2 + 4 <= data.length) {
      offset = 4;
    }
  }

  const remaining = data.length - offset;
  const numEntries = Math.floor(remaining / 2);
  if (numEntries < 256) return null; // Too small to be a valid LUT

  const lut = new Int16Array(numEntries);
  for (let i = 0; i < numEntries; i++) {
    lut[i] = dv.getInt16(offset + i * 2, true);
  }
  return lut;
}

// ---------------------------------------------------------------------------
// Internal: Temperature conversion
// ---------------------------------------------------------------------------

/**
 * Atmospheric transmission coefficient (τ) using simplified Beer-Lambert.
 * Reference: Thermimage R package — raw2temp.R
 */
function atmosphericTransmission(distance: number, humidity: number, airTemp: number): number {
  // Water vapor content (g/m³)
  const h2o = (humidity / 100) * Math.exp(
    1.5587
    + 0.06939 * airTemp
    - 0.00027816 * airTemp * airTemp
    + 0.00000068455 * airTemp * airTemp * airTemp
  );

  // Two-term atmospheric transmission
  const sqrtDist = Math.sqrt(distance / 2);
  const ata1 = 0.006569, ata2 = 0.012620;
  const atb1 = -0.002276, atb2 = -0.006670;
  const atx = 1.9;

  const tau = atx * Math.exp(-sqrtDist * (ata1 + atb1 * Math.sqrt(h2o)))
    + (1 - atx) * Math.exp(-sqrtDist * (ata2 + atb2 * Math.sqrt(h2o)));

  return Math.max(0.01, Math.min(1.0, tau));
}

/**
 * Apply environmental corrections to a base temperature reading.
 *
 * The curve LUT gives temperatures calibrated for default conditions
 * (emissivity=1, no atmosphere). We correct for:
 *   1. Atmospheric absorption (distance, humidity, air temp)
 *   2. Surface emissivity (< 1 means some reflected radiation)
 *   3. Reflected temperature from surroundings
 *
 * Uses a linearized radiance-space correction for computational efficiency.
 */
function correctTemperature(
  baseTempC: number,
  baseParams: RadiometricParams,
  userParams: RadiometricParams,
): number {
  // If user params match embedded (defaults), no correction needed
  if (
    Math.abs(userParams.emissivity - baseParams.emissivity) < 0.001 &&
    Math.abs(userParams.distance_m - baseParams.distance_m) < 0.1 &&
    Math.abs(userParams.humidity_pct - baseParams.humidity_pct) < 0.5 &&
    Math.abs(userParams.air_temp_c - baseParams.air_temp_c) < 0.5 &&
    Math.abs(userParams.reflected_temp_c - baseParams.reflected_temp_c) < 0.5
  ) {
    return baseTempC;
  }

  const eps = userParams.emissivity;
  const tau = atmosphericTransmission(
    userParams.distance_m,
    userParams.humidity_pct,
    userParams.air_temp_c,
  );

  const tObj = baseTempC + 273.15;
  const tAtm = userParams.air_temp_c + 273.15;
  const tRefl = userParams.reflected_temp_c + 273.15;

  // Radiance approximation using Stefan-Boltzmann (T^4 proportional to radiance)
  const rTotal = tObj ** 4;
  const rAtm = tAtm ** 4;
  const rRefl = tRefl ** 4;

  // Isolate object radiance from total measured signal
  const rObj = (rTotal - (1 - tau) * rAtm - (1 - eps) * tau * rRefl) / (eps * tau);

  if (rObj <= 0) return baseTempC; // Fallback if correction yields negative radiance

  const correctedK = Math.pow(rObj, 0.25);
  return correctedK - 273.15;
}

/**
 * Find min and max of a Uint16Array.
 */
function rawRange(data: Uint16Array): { rawMin: number; rawMax: number } {
  let rawMin = data[0], rawMax = data[0];
  for (let i = 1; i < data.length; i++) {
    if (data[i] < rawMin) rawMin = data[i];
    if (data[i] > rawMax) rawMax = data[i];
  }
  return { rawMin, rawMax };
}

/**
 * Convert RAW16 → Float32 temperatures (Celsius).
 *
 * DJI R-JPEG raw sensor values are stored as uint16 with 2 LSB padding bits
 * (i.e. the 14-bit sensor value is left-shifted by 2). The raw values encode
 * temperature in units of Kelvin × 64:
 *
 *   temperature_C = stored_uint16 / 64.0 - 273.15
 *
 * This gives ~2-4°C RMSE vs DJI IRP SDK ground truth — accurate enough for
 * field inspection. The APP5 curve LUT is NOT used because its structure is
 * non-monotonic and part of the SDK's proprietary calibration pipeline; it
 * cannot be reliably indexed by raw sensor values.
 *
 * Environmental corrections (emissivity, atmospheric transmission, reflected
 * temperature) are applied on top of the base conversion when user params
 * differ from embedded camera defaults.
 */
export function computeTemperatures(
  rawData: Uint16Array,
  _curveLut: Int16Array | null,
  embeddedParams: RadiometricParams,
  userParams: RadiometricParams,
): Float32Array {
  const n = rawData.length;
  const temps = new Float32Array(n);
  if (n === 0) return temps;

  // Base conversion: raw uint16 → Celsius via Kelvin×64 encoding
  for (let i = 0; i < n; i++) {
    temps[i] = rawData[i] / 64.0 - 273.15;
  }

  // Apply environmental corrections if user params differ from embedded
  const needsCorrection =
    Math.abs(userParams.emissivity - embeddedParams.emissivity) > 0.001 ||
    Math.abs(userParams.distance_m - embeddedParams.distance_m) > 0.1 ||
    Math.abs(userParams.humidity_pct - embeddedParams.humidity_pct) > 0.5 ||
    Math.abs(userParams.air_temp_c - embeddedParams.air_temp_c) > 0.5 ||
    Math.abs(userParams.reflected_temp_c - embeddedParams.reflected_temp_c) > 0.5;

  if (needsCorrection) {
    for (let i = 0; i < n; i++) {
      temps[i] = correctTemperature(temps[i], embeddedParams, userParams);
    }
  }

  const { rawMin, rawMax } = rawRange(rawData);
  const stats = arrayStats(temps);
  console.log(
    `[DJI Thermal] raw range: ${rawMin}-${rawMax}, ` +
    `temp range: ${stats.min.toFixed(2)}°C to ${stats.max.toFixed(2)}°C`
  );

  return temps;
}

// ---------------------------------------------------------------------------
// Public: Parse DJI R-JPEG
// ---------------------------------------------------------------------------

/**
 * Parse a DJI thermal R-JPEG file entirely in the browser.
 *
 * @param buffer - Raw file bytes (e.g. from File.arrayBuffer())
 * @returns Parsed thermal image with temperature data
 * @throws Error if the file is not a valid DJI R-JPEG
 */
export async function parseDjiThermal(
  buffer: ArrayBuffer,
  userParams?: Partial<RadiometricParams>,
): Promise<ThermalImage> {
  const segments = extractJpegSegments(buffer);

  // 1. Collect all APP3 segments → RAW16 data
  const app3Segments = segments.filter((s) => s.marker === 0xE3);
  if (app3Segments.length === 0) {
    throw new Error('No thermal data found (no APP3 segments). This may not be a DJI R-JPEG file.');
  }

  const totalRawBytes = app3Segments.reduce((sum, s) => sum + s.data.length, 0);
  const rawBuffer = new Uint8Array(totalRawBytes);
  let offset = 0;
  for (const seg of app3Segments) {
    rawBuffer.set(seg.data, offset);
    offset += seg.data.length;
  }

  // 2. Parse APP4 → calibration + camera model
  const app4Segment = segments.find((s) => s.marker === 0xE4);
  const calibration = app4Segment ? parseApp4(app4Segment.data) : {
    params: { ...DEFAULT_PARAMS },
    cameraModel: 'Unknown',
    width: 640,
    height: 512,
  };

  // 3. Parse APP5 → curve LUT
  const app5Segment = segments.find((s) => s.marker === 0xE5);
  const curveLut = app5Segment ? parseCurveLut(app5Segment.data) : null;

  // Visible JPEG dimensions (may exceed the raw matrix on super-res photos)
  const sofDims = parseSofDimensions(segments);

  // 4. Determine resolution
  const numUint16 = Math.floor(totalRawBytes / 2);
  let width = calibration.width;
  let height = calibration.height;

  // Verify resolution matches data, try common resolutions
  const commonResolutions: [number, number][] = [
    [640, 512],   // Most DJI cameras
    [1280, 1024], // H30T high-res
    [336, 256],   // Older/compact
    [320, 256],
    [160, 128],
    [128, 128],   // XTS
  ];

  if (width * height !== numUint16) {
    let found = false;
    for (const [w, h] of commonResolutions) {
      if (w * h === numUint16) {
        width = w;
        height = h;
        found = true;
        break;
      }
    }
    if (!found) {
      // Heuristic: if data has a small header, try trimming
      for (const trimBytes of [225, 2, 4, 8]) {
        const adjusted = Math.floor((totalRawBytes - trimBytes) / 2);
        for (const [w, h] of commonResolutions) {
          if (w * h === adjusted) {
            width = w;
            height = h;
            // Trim raw buffer
            const trimmedU8 = rawBuffer.slice(trimBytes);
            const rawData = new Uint16Array(trimmedU8.buffer, trimmedU8.byteOffset, adjusted);
            const params: RadiometricParams = { ...calibration.params, ...userParams };
            const temperatures = computeTemperatures(rawData, curveLut, calibration.params, params);
            const { min, max, avg } = arrayStats(temperatures);

            return {
              width, height,
              displayWidth: sofDims?.width ?? width,
              displayHeight: sofDims?.height ?? height,
              cameraModel: calibration.cameraModel,
              rawData,
              curveLut,
              temperatures,
              minTemp: min,
              maxTemp: max,
              avgTemp: avg,
              embeddedParams: calibration.params,
            };
          }
        }
      }
      // Last resort: assume 640×512 and truncate/pad
      width = 640;
      height = 512;
    }
  }

  // Create Uint16Array from concatenated raw bytes
  const expectedPixels = width * height;
  let rawData: Uint16Array;

  if (numUint16 >= expectedPixels) {
    // Aligned buffer
    if (rawBuffer.byteOffset % 2 === 0) {
      rawData = new Uint16Array(rawBuffer.buffer, rawBuffer.byteOffset, expectedPixels);
    } else {
      // Copy to aligned buffer
      const aligned = new ArrayBuffer(expectedPixels * 2);
      new Uint8Array(aligned).set(rawBuffer.slice(0, expectedPixels * 2));
      rawData = new Uint16Array(aligned);
    }
  } else {
    // Pad with zeros if data is shorter than expected
    const padded = new Uint16Array(expectedPixels);
    const src = new Uint16Array(rawBuffer.buffer, rawBuffer.byteOffset, numUint16);
    padded.set(src);
    rawData = padded;
  }

  // 5. Compute temperatures
  const params: RadiometricParams = { ...calibration.params, ...userParams };
  const temperatures = computeTemperatures(rawData, curveLut, calibration.params, params);
  const { min, max, avg } = arrayStats(temperatures);

  return {
    width,
    height,
    displayWidth: sofDims?.width ?? width,
    displayHeight: sofDims?.height ?? height,
    cameraModel: calibration.cameraModel,
    rawData,
    curveLut,
    temperatures,
    minTemp: min,
    maxTemp: max,
    avgTemp: avg,
    embeddedParams: calibration.params,
  };
}

// ---------------------------------------------------------------------------
// Public: ROI and Point queries
// ---------------------------------------------------------------------------

/**
 * Compute temperature statistics for a rectangular ROI.
 * Coordinates are in image pixel space (0-based).
 */
export function getRoiStats(
  temps: Float32Array,
  imgWidth: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): RoiStats {
  const left = Math.max(0, Math.min(Math.floor(Math.min(x0, x1)), imgWidth - 1));
  const right = Math.max(0, Math.min(Math.floor(Math.max(x0, x1)), imgWidth - 1));
  const imgHeight = Math.floor(temps.length / imgWidth);
  const top = Math.max(0, Math.min(Math.floor(Math.min(y0, y1)), imgHeight - 1));
  const bottom = Math.max(0, Math.min(Math.floor(Math.max(y0, y1)), imgHeight - 1));

  let min = Infinity;
  let max = -Infinity;
  let maxX = left;
  let maxY = top;
  let sum = 0;
  let sumSq = 0;
  let n = 0;

  // Collect all pixel temperatures for median calculation
  const values: number[] = [];

  for (let y = top; y <= bottom; y++) {
    const rowOffset = y * imgWidth;
    for (let x = left; x <= right; x++) {
      const t = temps[rowOffset + x];
      if (t < min) min = t;
      if (t > max) { max = t; maxX = x; maxY = y; }
      sum += t;
      sumSq += t * t;
      values.push(t);
      n++;
    }
  }

  if (n === 0) {
    return { min_c: 0, max_c: 0, avg_c: 0, std_c: 0, n: 0, max_x: 0, max_y: 0, ref_c: 0, delta_t: 0 };
  }

  const avg_c = sum / n;
  const variance = sumSq / n - avg_c * avg_c;
  const std_c = Math.sqrt(Math.max(0, variance));

  // Median = reference temperature (represents a normal/healthy cell)
  values.sort((a, b) => a - b);
  const mid = Math.floor(n / 2);
  const ref_c = n % 2 === 0 ? (values[mid - 1] + values[mid]) / 2 : values[mid];

  const delta_t = max - ref_c;

  return { min_c: min, max_c: max, avg_c, std_c, n, max_x: maxX, max_y: maxY, ref_c, delta_t };
}

/**
 * Get temperature at a single pixel.
 */
export function getPointTemp(
  temps: Float32Array,
  imgWidth: number,
  x: number,
  y: number,
): number {
  const px = Math.max(0, Math.min(Math.round(x), imgWidth - 1));
  const imgHeight = Math.floor(temps.length / imgWidth);
  const py = Math.max(0, Math.min(Math.round(y), imgHeight - 1));
  return temps[py * imgWidth + px];
}

/**
 * ROI stats with coordinates in DISPLAY (visible JPEG) pixel space.
 *
 * Scales the ROI onto the radiometric matrix before sampling and maps the
 * hottest-pixel location back to display space (centered on the matrix
 * pixel's display footprint), so annotation coords, persisted thermalData,
 * and crosshair rendering all stay in one coordinate space. Scale is 1:1
 * for native-resolution photos.
 */
export function getRoiStatsDisplay(
  ti: ThermalImage,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): RoiStats {
  const kx = ti.width / (ti.displayWidth || ti.width);
  const ky = ti.height / (ti.displayHeight || ti.height);
  const stats = getRoiStats(ti.temperatures, ti.width, x0 * kx, y0 * ky, x1 * kx, y1 * ky);
  return {
    ...stats,
    max_x: (stats.max_x + 0.5) / kx,
    max_y: (stats.max_y + 0.5) / ky,
  };
}

/**
 * Point temperature with coordinates in DISPLAY (visible JPEG) pixel space.
 * Floors to the matrix pixel whose display footprint contains the point.
 */
export function getPointTempDisplay(ti: ThermalImage, x: number, y: number): number {
  const kx = ti.width / (ti.displayWidth || ti.width);
  const ky = ti.height / (ti.displayHeight || ti.height);
  return getPointTemp(ti.temperatures, ti.width, Math.floor(x * kx), Math.floor(y * ky));
}

/**
 * Recalculate all temperatures with new parameters.
 * Reuses the cached rawData + curveLut from a previous parse.
 */
export function recalcTemperatures(
  thermalImage: ThermalImage,
  newParams: RadiometricParams,
): Float32Array {
  return computeTemperatures(
    thermalImage.rawData,
    thermalImage.curveLut,
    thermalImage.embeddedParams,
    newParams,
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function arrayStats(arr: Float32Array): { min: number; max: number; avg: number } {
  if (arr.length === 0) return { min: 0, max: 0, avg: 0 };
  let min = arr[0], max = arr[0], sum = 0;
  for (let i = 0; i < arr.length; i++) {
    const v = arr[i];
    if (v < min) min = v;
    if (v > max) max = v;
    sum += v;
  }
  return { min, max, avg: sum / arr.length };
}
